import type { WardrobeModule } from "../types";
import { defaultShelvingCell, getShelvingDiagonalSections, getShelvingGridLayout, normalizeShelvingDiagonalCells, shelvingCellId } from "./shelving";

/** Inclusive grid bounds, rows counted from the floor. */
export interface DiagonalArea { left: number; right: number; bottom: number; top: number }
export const diagonalAreaFromCorners = (a: { column: number; row: number }, b: { column: number; row: number }): DiagonalArea => ({
  left: Math.min(a.column, b.column), right: Math.max(a.column, b.column),
  bottom: Math.min(a.row, b.row), top: Math.max(a.row, b.row)
});
export const areaContainsCell = (area: DiagonalArea, column: number, row: number) =>
  column >= area.left && column <= area.right && row >= area.bottom && row <= area.top;

export function inspectDiagonalArea(module: WardrobeModule, area: DiagonalArea, sourceId?: string) {
  const grid = getShelvingGridLayout(module);
  const valid = Object.values(area).every(Number.isInteger) && area.left >= 0 && area.bottom >= 0 &&
    area.right < grid.columns && area.top < grid.rows && area.left <= area.right && area.bottom <= area.top;
  const sections = getShelvingDiagonalSections(module);
  const source = sections.find(section => section.cell.id === sourceId);
  const overlaps = sections.filter(section => section.cell.id !== sourceId &&
    section.column <= area.right && section.column + section.columnSpan - 1 >= area.left &&
    section.row <= area.top && section.row + section.rowSpan - 1 >= area.bottom);
  const partialOverlap = overlaps.some(section => !areaContainsCell(area, section.column, section.row) ||
    !areaContainsCell(area, section.column + section.columnSpan - 1, section.row + section.rowSpan - 1));
  const occupied = (module.shelvingCells ?? []).filter(cell => cell.content !== "diagonal" &&
    areaContainsCell(area, cell.column, cell.row) && (cell.content !== "open" || cell.extraShelf ||
      cell.extraDivider || cell.backPanel || Math.abs(cell.leftDividerAngle ?? 0) >= 0.5));
  const error = !valid ? "Область выходит за границы стеллажа." : sourceId && !source
    ? "Секция изменилась. Выберите её заново." : partialOverlap
      ? "Соседняя диагональная секция попала в область частично. Выделите её целиком для объединения или оставьте за границей." : null;
  return { error, source, overlaps, occupied, needsConfirmation: overlaps.length > 0 || occupied.length > 0 };
}

/** Atomic transaction: no contents are changed until all conflicts are approved. */
export function applyDiagonalArea(module: WardrobeModule, area: DiagonalArea, sourceId?: string, replaceExisting = false) {
  const inspection = inspectDiagonalArea(module, area, sourceId);
  if (inspection.error || (inspection.needsConfirmation && !replaceExisting)) return null;
  const removedIds = new Set([sourceId, ...inspection.overlaps.map(section => section.cell.id)]);
  const cells = (module.shelvingCells ?? []).filter(cell => !removedIds.has(cell.id) &&
    !areaContainsCell(area, cell.column, cell.row));
  const anchor = {
    ...defaultShelvingCell(module.id, area.left, area.bottom),
    ...inspection.source?.cell,
    id: shelvingCellId(module.id, area.left, area.bottom), column: area.left, row: area.bottom,
    content: "diagonal" as const, extraShelf: false, extraDivider: false,
    backPanel: Boolean(inspection.source?.cell.backPanel || inspection.overlaps.some(section => section.cell.backPanel)),
    diagonalColumnSpan: area.right - area.left + 1, diagonalRowSpan: area.top - area.bottom + 1
  };
  const grid = getShelvingGridLayout(module);
  return { ...module, shelvingCells: normalizeShelvingDiagonalCells(module.id, [...cells, anchor], grid.columns, grid.rows) };
}
